/*----------------------------------
- TLD PAGES: NEWSLETTER BAND
----------------------------------*/

/**
 * The agenda's capture band on a TLD page, on the marketing layer (the mock's `wp-nl`, website-tld-insight.html): the
 * agenda's heading and promise, the field and Subscribe, fed by the shared subscribe hook; the outcome lines and the
 * anti-bot decoy are the newsletter page's (`newsletterOutcome.ts`, `NewsletterDecoy`). It replaces the old ink band
 * (`NewsletterInkBand`) on the insight page and the hub, each under its own source.
 *
 * @docs docs/features/tld-intelligence
 * @rule The request, the timing signal and the status are `useNewsletterSubscribe`'s; the band records the accepted
 *       subscription as `newsletter_subscribe_submitted` with its source only, never the address, then hands the page
 *       its own callback (the insight page's `newsletter` call to action, D15).
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import type { NewsletterPublicSubscriptionSource } from '@uniquedomains/components/contracts/newsletter/public';
import { NewsletterBand } from '@uniquedomains/components/marketing/newsletter-band/NewsletterBand';

import { AGENDA_NEWSLETTER_BAND_CONTENT } from '@/client/catalogs/landing/agenda/pageContent';
import { NEWSLETTER_PAGE_CONTENT } from '@/client/catalogs/landing/newsletter/pageContent';
import { TLD_HUB_CONTENT } from '@/client/catalogs/landing/tld/hubContent';
import { useNewsletterSubscribe } from '@/client/pages/landing/components/newsletter/useNewsletterSubscribe';
import { track } from '@/client/pages/landing/lib/analytics';
import NewsletterDecoy from '@/client/pages/landing/newsletter/page/NewsletterDecoy';
import { toNewsletterCaptureOutcome } from '@/client/pages/landing/newsletter/page/newsletterOutcome';

/*----------------------------------
- TYPES
----------------------------------*/

type TldNewsletterBandProps = {
    /** The band's anchor, which a page's own link may scroll to. */
    anchorId: string;
    source: NewsletterPublicSubscriptionSource;
    /** A page's own analytics beside the band's, once the list accepted the address. */
    onSubscribed?: () => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldNewsletterBand({ anchorId, source, onSubscribed }: TldNewsletterBandProps) {
    const { email, setEmail, honeypot, setHoneypot, status, submit } = useNewsletterSubscribe(source, () => {
        track('newsletter_subscribe_submitted', { source });
        onSubscribed?.();
    });
    const outcome = toNewsletterCaptureOutcome(status);
    const fieldId = `newsletter-email-${anchorId}`;
    const titleId = `${anchorId}-title`;

    return (
        <NewsletterBand
            id={anchorId}
            as="section"
            aria-labelledby={titleId}
            titleId={titleId}
            title={AGENDA_NEWSLETTER_BAND_CONTENT.heading}
            body={AGENDA_NEWSLETTER_BAND_CONTENT.body}
            fieldId={fieldId}
            fieldLabel={NEWSLETTER_PAGE_CONTENT.form.fieldLabel}
            placeholder={NEWSLETTER_PAGE_CONTENT.form.placeholder}
            email={email}
            onEmailInput={setEmail}
            submitLabel={TLD_HUB_CONTENT.newsletter.submit}
            onSubmit={() => void submit()}
            status={outcome.status}
            message={outcome.message}
            decoy={<NewsletterDecoy fieldId={fieldId} value={honeypot} onInput={setHoneypot} />}
            data-testid={`newsletter-ink-band-${source}`}
            formTestId={`newsletter-subscribe-form-${anchorId}`}
            messageTestId={outcome.refused ? `newsletter-subscribe-feedback-${anchorId}` : `newsletter-subscribe-confirmation-${anchorId}`}
        />
    );
}
